import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertTitle, Box, Button, ButtonGroup, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH, PENSION_PAYOUT_INTEREST_RATE } from "Components/Analyses/constants";
import { BarChartIcon, LineChartIcon, ResetIcon } from "Components/_Shared/Icons/Icons";
import { StyledHtmlLink } from "Components/_Shared/Routing/UnstyledLink";
import {
  type PensionGoalFormContext,
  type PensionGoalFormType,
  getPensionGoalFormSchema,
  minDate,
  minRetirementDate,
} from "Forms/Schemas/Goals/pensionGoalFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetPensionGoalQueryKey,
  useCreatePensionGoal,
  useGetAnalysisClients,
  useUpdatePensionGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemRelationType, type PensionGoalDto, PensionGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  DEFAULT_PENSION_DURATION,
  INTEREST_RATE_AGGRESIVE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { format, isBefore, parseISO, startOfDay } from "date-fns";
import { forwardRef, useContext, useId, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePensionCalculations } from "../Hooks/usePensionCalculations";
import { ModelingAccordion } from "./ModelingAccordion";
import { PensionChart } from "./PensionChart";
import { PensionDifferenceChart } from "./PensionDifferenceChart";
import { PensionGoalModelingHint } from "./PensionGoalModelingHint";
import { buildPensionGoalData, buildPensionGoalModeling } from "./pensionGoalPayload";
import { ageMonthsToYears, retirementStartAgeToMonths } from "./pensionRetirementDate";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: PensionGoalDto;
  readonly goalId?: string;
};

export const PensionGoalForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const [chartType, setChartType] = useState<"line" | "bar">("line");
  const retirementDateHintId = useId();

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);
  const birthDate = clientsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client)?.birthDate;
  const clientBirthDate = useMemo(() => (birthDate ? parseISO(birthDate) : null), [birthDate]);
  const isClientBirthDateMissing = !!clientsData && !clientBirthDate;

  const retirementStartAgeMonths = retirementStartAgeToMonths(editGoal?.data.retirementStartAge);

  const { mutate: createPensionGoal } = useCreatePensionGoal();

  const { mutate: updatePensionGoal } = useUpdatePensionGoal();

  const methods = useForm<PensionGoalFormType>({
    resolver: yupResolver(getPensionGoalFormSchema(t)),
    context: { hasClientBirthDate: !!clientBirthDate } satisfies PensionGoalFormContext,
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          infinitePension: editGoal.data.isInfinityRentRequired ?? false,
          includeStatePension: editGoal.data.isRetirementPensionExpected ?? false,
          includeInflation: editGoal.modeling.inflation?.enabled,
          includeFee: editGoal.modeling.fee?.enabled,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          inflationRate: (editGoal.modeling.inflation?.rate?.toString() ?? "0") as any,
          feeRate: (editGoal.modeling.fee?.rate?.toString() ?? "0") as any,
          privatePensionDuration: editGoal.data.retirementPayoutInterval?.value.toString() as any,
          pensionInterestRate: editGoal.modeling.payoutAppreciationRate?.toString() as any,
          privatePensionStartAge:
            retirementStartAgeMonths === undefined ? undefined : (ageMonthsToYears(retirementStartAgeMonths).toString() as any),
          privatePensionStartAgeMonths: retirementStartAgeMonths,
          privatePensionStartDate: null,
          statePensionAmount: editGoal.data.expectedRetirementAmount?.value.toString() as any,
          privatePensionAmount: editGoal.data.rentAmount?.value.toString() as any,
          // `new Date(null)` is the epoch, not an empty date — casting the null away here used to silently seed the
          // field with 1.1.1970, which then tripped the "draw start date is in the past" guard forever. Keep it
          // null and let the server's derived default fill it in.
          statePensionStart: editGoal.data.retirementStartDate ? parseISO(editGoal.data.retirementStartDate) : null,
          monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value.toString() as any,
          targetAmount: editGoal.modeling.targetAmount?.value.toString() as any,
          isTargetAmountManual: editGoal.modeling.isTargetAmountManual ?? false,
          isInvestmentAmountManual: editGoal.modeling.isInvestmentAmountManual ?? false,
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === PensionGoalDtoPriorityLevel.high,
          netIncomeAmount: editGoal.data.netSalaryAmount?.value.toString() as any,
          grossIncomeAmount: editGoal.data.grossSalaryAmount?.value.toString() as any,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          name: t(Resources.AnalysisDetail.Goals.GoalType.Pension),
          infinitePension: false,
          includeStatePension: false,
          includeInflation: true,
          includeFee: true,
          interestRate: INTEREST_RATE_AGGRESIVE.toString() as any,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          feeRate: DEFAULT_FEE_RATE.toString() as any,
          privatePensionDuration: DEFAULT_PENSION_DURATION.toString() as any,
          pensionInterestRate: PENSION_PAYOUT_INTEREST_RATE.toString() as any,
          referenceStartDate: new Date(),
          privatePensionStartDate: null,
          statePensionStart: null,
          isTargetAmountManual: false,
          isInvestmentAmountManual: false,
        },
  });

  const {
    control,
    setValue,
    watch,
    getValues,
    resetField,
    formState: { dirtyFields },
    handleSubmit,
    clearErrors,
    trigger,
  } = methods;

  // data
  const infinitePension = watch("infinitePension");
  const includeStatePension = watch("includeStatePension");
  const netIncomeAmount = watch("netIncomeAmount");
  const statePensionAmount = watch("statePensionAmount");
  const statePensionStart = watch("statePensionStart");
  const privatePensionAmount = watch("privatePensionAmount");
  const privatePensionDuration = watch("privatePensionDuration");

  // modeling
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const targetAmount = watch("targetAmount");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const pensionInterestRate = watch("pensionInterestRate");

  const referenceStartDate = watch("referenceStartDate");
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const isInvalidDrawStartDate = statePensionStart && isBefore(startOfDay(statePensionStart), startOfDay(minDate));

  const {
    preview,
    calculations: { clientAge, yearsRemaining, isWithdrawBeforeStatePensionRequired },
    handlers: {
      handleInfinitePensionClick,
      handleIncludeStatePensionClick,
      handleRetirementAgeChange,
      handleRetirementAgeBlur,
      handleRetirementDateChange,
      handleRetirementDateBlur,
      handlePrivatePensionDurationBlur,
      handleNetIncomeAmountBlur,
      handlePrivatePensionAmountBlur,
      handleStatePensionAmountBlur,
      handleInflationBlur,
      handleWithdrawInterestRateBlur,
      handleIncludeInflationClick,
      handleIncludeFeeChange,
      handleFeeRateBlur,
      handleInterestRateBlur,
      // handleTargetAmountBlur,
      // handleMonthlyContributionWithFeeBlur,
      handleResetToDefaults,
    },
  } = usePensionCalculations({
    analysisId,
    versionId,
    goalGuid: editGoal?.goalId,
    watch,
    setValue,
    getValues,
    resetField,
    clearErrors,
    trigger,
    dirtyFields,
    isEditMode: !!editGoal,
    isInvalidDrawStartDate: !!isInvalidDrawStartDate,
    clientBirthDate,
  });

  const onCreateGoal = (data: PensionGoalFormType) => {
    createPensionGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? PensionGoalDtoPriorityLevel.high : PensionGoalDtoPriorityLevel.low,
          data: buildPensionGoalData(data),
          modeling: buildPensionGoalModeling(data),
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: PensionGoalFormType) => {
    updatePensionGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? PensionGoalDtoPriorityLevel.high : PensionGoalDtoPriorityLevel.low,
          data: buildPensionGoalData(data),
          modeling: buildPensionGoalModeling(data),
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetPensionGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  // "Do we have enough input to model this?" used to be answered here by testing the individual fields. The server
  // answers it now: it nulls out what it can't compute yet, so anything else is second-guessing it — and the field
  // tests got it wrong anyway, since they were truthiness checks on strings where a legitimate "0" reads as false.
  const shouldRenderModelingAndChart = () => {
    if (isInvalidDrawStartDate) return false;

    return !!preview && preview.targetAmount !== null && preview.retirementMonth !== null;
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Pension)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Stack spacing={Spacing.S}>
                  <FormSwitch
                    disabled={readOnly}
                    control={control}
                    name="infinitePension"
                    label={t(Resources.AnalysisDetail.Goals.InfinitePension)}
                    onChange={() => {
                      const newInfinitePensionValue = !infinitePension;
                      handleInfinitePensionClick(newInfinitePensionValue);
                    }}
                  />
                  <FormSwitch
                    control={control}
                    disabled={readOnly}
                    name="includeStatePension"
                    label={t(Resources.AnalysisDetail.Goals.IncludeStatePension)}
                    onChange={() => {
                      const newIncludeStatePensionValue = !includeStatePension;
                      handleIncludeStatePensionClick(newIncludeStatePensionValue);
                    }}
                  />
                </Stack>
              </Grid>
              <Grid size={12}>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      disabled={readOnly}
                      name="privatePensionStartAge"
                      label={t(Resources.AnalysisDetail.Goals.RetirementAge)}
                      required
                      valueType="text"
                      scale={0}
                      min={clientAge}
                      max={100}
                      thousandsSeparator=""
                      unit={t(Resources.Common.Years)}
                      fullWidth
                      // TextField points aria-describedby at its own error text; overriding it must keep that link.
                      slotProps={{ htmlInput: { "aria-describedby": `${retirementDateHintId} privatePensionStartAge-helper-text` } }}
                      onChange={handleRetirementAgeChange}
                      onBlur={handleRetirementAgeBlur}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormDatePicker
                      control={control}
                      name="privatePensionStartDate"
                      label={t(Resources.AnalysisDetail.Goals.RetirementDate)}
                      required={!!clientBirthDate}
                      views={["year", "month"]}
                      minDate={minRetirementDate}
                      disabled={readOnly || !clientBirthDate}
                      onChange={handleRetirementDateChange}
                      onBlur={handleRetirementDateBlur}
                      ariaDescribedBy={retirementDateHintId}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
                {(clientBirthDate || isClientBirthDateMissing) && (
                  <Typography id={retirementDateHintId} variant="caption" color="text.secondary" mt={Spacing.S}>
                    {clientBirthDate
                      ? t(Resources.AnalysisDetail.Goals.RetirementAgeDateHint, { birthDate: format(clientBirthDate, "MM/yyyy") })
                      : t(Resources.AnalysisDetail.Goals.RetirementDateMissingBirthDate)}
                  </Typography>
                )}
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="privatePensionDuration"
                  label={t(Resources.AnalysisDetail.Goals.PensionDuration)}
                  required
                  valueType="text"
                  scale={0}
                  min={1}
                  max={100}
                  thousandsSeparator=""
                  unit={t(Resources.Common.Years)}
                  disabled={readOnly || infinitePension}
                  onBlur={handlePrivatePensionDurationBlur}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="privatePensionAmount"
                  label={t(Resources.AnalysisDetail.Goals.MonthlyPensionAmount)}
                  required
                  valueType="text"
                  scale={2}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={handlePrivatePensionAmountBlur}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          {includeStatePension && (
            <Box component="section" p={Spacing.XL} borderRadius={Radius.L} sx={{ bgcolor: colors.backgroundSecondary }}>
              <HStack justifyContent="space-between" alignItems="center" mb={Spacing.XL}>
                <Typography component="h3" variant="h3">
                  {t(Resources.AnalysisDetail.Goals.StatePensionSectionTitle)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={readOnly}
                  color="secondary"
                  endIcon={<ResetIcon sx={{ color: "text.primary" }} />}
                  onClick={handleResetToDefaults}
                >
                  {t(Resources.AnalysisDetail.Goals.ResetToDefaultValues)}
                </Button>
              </HStack>
              <Grid container spacing={Spacing.XL}>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="netIncomeAmount"
                    label={t(Resources.AnalysisDetail.Goals.NetIncome)}
                    required
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    onBlur={handleNetIncomeAmountBlur}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    disabled={readOnly}
                    name="statePensionAmount"
                    label={t(Resources.AnalysisDetail.Goals.StatePensionAmount)}
                    required
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    onBlur={handleStatePensionAmountBlur}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormDatePicker
                    control={control}
                    name="statePensionStart"
                    label={t(Resources.AnalysisDetail.Goals.StatePensionStart)}
                    disabled
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" mt={Spacing.M}>
                {t(Resources.AnalysisDetail.Goals.StatePensionDerivedFromNetIncome)}{" "}
                {t(Resources.AnalysisDetail.Goals.DetailsCanBeFoundOnSite)}{" "}
                <StyledHtmlLink target="_blank" rel="noopener" href="https://www.cssz.cz/">
                  {t(Resources.AnalysisDetail.Goals.Cssz)}
                </StyledHtmlLink>
                .
              </Typography>
            </Box>
          )}
          <Box>
            {!!preview && !!statePensionStart && shouldRenderModelingAndChart() ? (
              <Box mb={Spacing.XL}>
                <PensionGoalModelingHint
                  feeRate={Number(feeRate)}
                  inflationRate={Number(inflationRate)}
                  interestRate={Number(interestRate)}
                  pensionInterestRate={Number(pensionInterestRate)}
                  targetAmount={Number(targetAmount)}
                  monthlyContribution={Number(monthlyContributionWithFee)}
                  yearsRemaining={yearsRemaining}
                  pensionAmount={Number(privatePensionAmount)}
                  pensionDuration={Number(privatePensionDuration)}
                  infinitePension={infinitePension}
                />
              </Box>
            ) : null}
            {shouldRenderModelingAndChart() ? (
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <FormNumberField
                      disabled
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      valueType="text"
                      scale={2}
                      min={0}
                      thousandsSeparator=" "
                      radix=","
                      mapToRadix={["."]}
                      unit={t(Resources.Common.CZKSuffix)}
                      fullWidth
                      // onBlur={() => handleMonthlyContributionWithFeeBlur(Number(monthlyContributionWithFee))}
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      disabled
                      control={control}
                      name="targetAmount"
                      label={t(Resources.AnalysisDetail.Goals.TargetAmount)}
                      valueType="text"
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      // onBlur={handleTargetAmountBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.PensionAccumulationInterestRate)}
                      required
                      valueType="text"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onBlur={handleInterestRateBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="pensionInterestRate"
                      label={t(Resources.AnalysisDetail.Goals.DrawdownInterestRate)}
                      required
                      valueType="text"
                      scale={1}
                      min={0}
                      thousandsSeparator=" "
                      unit="%"
                      disabled={readOnly}
                      onBlur={handleWithdrawInterestRateBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={readOnly}
                      onChange={handleIncludeInflationClick}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      valueType="text"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onBlur={handleInflationBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      disabled={readOnly}
                      onChange={handleIncludeFeeChange}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      valueType="text"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeFee || readOnly}
                      onBlur={handleFeeRateBlur}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={12}>
                    {preview?.phases && statePensionAmount && includeStatePension && isWithdrawBeforeStatePensionRequired && (
                      <Alert severity="info">
                        <AlertTitle>{t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.Title)}</AlertTitle>
                        {t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.FirstPhrase)}{" "}
                        <Typography display="inline" variant="body2" fontWeight={500}>
                          do {format(parseISO(preview.phases.statePensionStartDate), "dd.MM.yyyy")}
                        </Typography>
                        , {t(Resources.AnalysisDetail.Goals.PensionDifferenceHint.SecondPhrase)}{" "}
                        <Typography display="inline" variant="body2" fontWeight={500}>
                          {formatCurrency(preview.phases.monthlyWithdrawal, CurrencyCode.CZK)}
                        </Typography>
                        .
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </ModelingAccordion>
            ) : null}
          </Box>
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderModelingAndChart() && (
                <Grid size={12}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    <ButtonGroup sx={{ mb: theme.spacing(2) }}>
                      <Button
                        variant="outlined"
                        onClick={() => setChartType("line")}
                        sx={{
                          height: "40px",
                          borderRadius: theme.spacing(Radius.M),
                          borderColor: colors.borderLight,
                          bgcolor: chartType === "line" ? colors.backgroundSecondary : "transparent",
                          color: chartType === "line" ? colors.textPrimary : colors.textSecondary,
                          boxShadow: "none",
                        }}
                      >
                        <LineChartIcon />
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setChartType("bar")}
                        sx={{
                          height: "40px",
                          borderRadius: theme.spacing(Radius.M),
                          borderColor: colors.borderLight,
                          bgcolor: chartType === "bar" ? colors.backgroundSecondary : "transparent",
                          color: chartType === "bar" ? colors.textPrimary : colors.textSecondary,
                          boxShadow: "none",
                        }}
                      >
                        <BarChartIcon />
                      </Button>
                    </ButtonGroup>
                  </Box>
                  {chartType === "bar" && (
                    <PensionDifferenceChart netIncomeAmount={Number(netIncomeAmount)} statePensionAmount={Number(statePensionAmount)} />
                  )}
                  {chartType === "line" && (
                    <>
                      <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
                        <Stack spacing={Spacing.XS} padding={Spacing.S} display="flex" justifyContent="center" alignItems="center">
                          <Typography variant="caption" fontWeight={500}>
                            {t(Resources.Common.Investment)}
                          </Typography>
                          <Typography variant="caption">
                            {formatCurrency(monthlyContributionWithFee, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                          </Typography>
                        </Stack>
                        <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                          <Typography variant="caption" fontWeight={500}>
                            {t(Resources.AnalysisDetail.Goals.DrawProcess)}
                          </Typography>
                          <Typography variant="caption">
                            {formatCurrency(privatePensionAmount, CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                          </Typography>
                        </Stack>
                      </Box>
                      {preview && <PensionChart preview={preview} referenceStartDate={referenceStartDate} />}
                    </>
                  )}
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
